import { Component, EventEmitter, Output, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NbCardModule, NbInputModule, NbButtonModule, NbIconModule, NbSpinnerModule, NbTagModule, NbAutocompleteModule, NbTagInputAddEvent } from '@nebular/theme';

@Component({
  selector: 'app-module-menu',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    NbCardModule, 
    NbInputModule, 
    NbButtonModule, 
    NbIconModule,
    NbSpinnerModule,
    NbTagModule,
    NbAutocompleteModule
  ],
  templateUrl: './module-menu.component.html',
  styleUrl: './module-menu.component.scss'
})
export class ModuleMenuComponent implements OnInit, OnChanges {
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();
  @Output() openInEditor = new EventEmitter<number>();
  @Input() screenshotUrl: string | null = null;
  @Input() isLoading: boolean = false;
  @Input() availableTags: any[] = [];
  @Input() moduleToEdit: any = null;
  @Input() isEditMode: boolean = false;
  @Input() hideEditorActions: boolean = false;
  @Output() update = new EventEmitter<any>();
  @Output() delete = new EventEmitter<void>();
  form: FormGroup;
  selectedTags: Set<string> = new Set();
  filteredAvailableTags: any[] = [];

  constructor() {
    this.form = new FormGroup({
      name: new FormControl('', Validators.required),
      description: new FormControl('', [Validators.maxLength(500)]),
      tags: new FormControl('')
    });
  }

  ngOnInit() {
    this.updateForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['moduleToEdit'] && this.moduleToEdit) {
      this.updateForm();
    }
    if (changes['availableTags']) {
      this.updateFilteredTags();
    }
  }

  private updateFilteredTags(): void {
    this.filteredAvailableTags = this.availableTags.filter(
      t => !this.selectedTags.has(typeof t === 'object' && t.value ? t.value : t)
    );
  }

  private updateForm() {
    if (this.isEditMode && this.moduleToEdit) {
      this.form.patchValue({
        name: this.moduleToEdit.name,
        description: this.moduleToEdit.description
      });

      this.selectedTags.clear();
      if (this.moduleToEdit.tags) {
         this.moduleToEdit.tags.forEach((tag: any) => {
             const tagValue = typeof tag === 'object' && tag.value ? tag.value : tag;
             this.selectedTags.add(tagValue);
         });
         this.form.controls['tags'].setValue(Array.from(this.selectedTags).join(','));
         this.updateFilteredTags();
      }

    }
  }

  onClose() {
    this.close.emit();
  }

  onSave() {
    if (this.form.valid) {
      // Merge selected tags into form value or handle separately
      // The user likely wants these tags sent.
      const formData = {
          ...this.form.value,
          tags: Array.from(this.selectedTags)
      };
      
      if (this.isEditMode) {
          this.update.emit(formData);
      } else {
          this.save.emit(formData);
      }
    }
  }

  onDelete(): void {
    this.delete.emit();
  }

  onOpenInEditor(): void {
    if (this.moduleToEdit?.id) {
      this.openInEditor.emit(this.moduleToEdit.id);
    }
  }

  onTagAdd({ value, input }: NbTagInputAddEvent): void {
    if (value) {
      this.selectedTags.add(value);
      this.updateFilteredTags();
    }
    input.nativeElement.value = '';
    this.form.controls['tags'].setValue(Array.from(this.selectedTags).join(','));
  }

  onTagRemove(tagToRemove: string): void {
    this.selectedTags.delete(tagToRemove);
    this.updateFilteredTags();
    this.form.controls['tags'].setValue(Array.from(this.selectedTags).join(','));
  }

  onTagSelectedWithInput(tag: any, input: any): void {
      if (tag) {
        const tagValue = typeof tag === 'object' && tag.value ? tag.value : tag;
        this.selectedTags.add(tagValue);
        this.updateFilteredTags();
        this.form.controls['tags'].setValue(Array.from(this.selectedTags).join(','));
        if (input && input.value !== undefined) {
            input.value = '';
        }
      }
  }
}
